import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Search, Loader2, ChevronRight } from 'lucide-react';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface ConversationRow {
  id: string;
  channel?: string | null;
  status?: string | null;
  subject?: string | null;
  from_identifier?: string | null;
  fromIdentifier?: string | null;
  last_message_at?: string | null;
  lastMessageAt?: string | null;
}

interface PaginatedResponse<T> {
  items: T[];
  total: number;
}

export default function ConversationsInboxPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ConversationRow[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ page: '1', limit: '50' });
    if (search) params.set('search', search);

    api.get<PaginatedResponse<ConversationRow>>(`/entities/conversations?${params.toString()}`)
      .then((res) => {
        if (cancelled) return;
        setRows(res.items ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        setRows([]);
        setError(err instanceof Error ? err.message : 'Failed to load inbox');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [search]);

  const title = useMemo(() => `Inbox${rows.length ? ` (${rows.length})` : ''}`, [rows.length]);

  return (
    <div className="space-y-4 p-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <MessageSquare className="h-5 w-5 text-sky-600" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search subject / sender / channel"
              className="pl-9"
            />
          </div>

          {loading ? (
            <div className="flex min-h-[180px] items-center justify-center text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading inbox…
            </div>
          ) : error ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {error}
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-lg border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
              No conversations found.
            </div>
          ) : (
            <div className="space-y-2">
              {rows.map((row) => {
                const sender = row.fromIdentifier ?? row.from_identifier ?? 'Unknown sender';
                const lastAt = row.lastMessageAt ?? row.last_message_at;
                return (
                  <button
                    key={row.id}
                    type="button"
                    className="flex w-full items-start gap-3 rounded-xl border bg-background px-4 py-3 text-left hover:bg-muted/40"
                    onClick={() => navigate(`/chat/${row.id}`)}
                  >
                    <div className="mt-0.5 rounded-lg bg-sky-50 p-2 text-sky-700">
                      <MessageSquare className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="truncate font-medium">{row.subject || 'Untitled conversation'}</p>
                        {row.status ? <Badge variant="outline" className="text-[10px]">{row.status}</Badge> : null}
                      </div>
                      <p className="mt-1 truncate text-xs text-muted-foreground">{sender}</p>
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        {row.channel ? `${row.channel.toUpperCase()} · ` : ''}{lastAt ? new Date(lastAt).toLocaleString() : 'No messages yet'}
                      </p>
                    </div>
                    <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
